import { strings } from '@/content/strings';
import { cssVars } from '@/lib/cssVars';
import { cx } from '@/lib/cx';
import styles from './PhonemeStrip.module.css';

export interface StripSegment {
  /** Spelling shown on the chip, e.g. "ck". */
  letters: string;
  /** The sound being practised; ringed in green and lit green when produced correctly. */
  target: boolean;
}

type ChipState = 'waiting' | 'correct' | 'practise';

interface PhonemeStripProps {
  segments: readonly StripSegment[];
  /**
   * Per-segment verdicts from the latest attempt: true = sounded right, false = to practise,
   * undefined = not judged. Omit before an attempt.
   */
  results?: readonly (boolean | undefined)[] | undefined;
}

function chipState(passed: boolean | undefined): ChipState {
  if (passed === undefined) return 'waiting';
  return passed ? 'correct' : 'practise';
}

/**
 * A word split into its sounds, one chip per spelling unit. The target sound is always ringed in
 * green. After an attempt the chips light up left to right: green for a correct target sound,
 * ice-blue for other correct sounds, amber for sounds to practise. Only colour and glow change, so
 * nothing on the strip moves.
 */
export function PhonemeStrip({ segments, results }: PhonemeStripProps) {
  const f = strings.feedback;
  return (
    <ol
      className={styles.strip}
      aria-label={f.soundsLabel}
      style={cssVars({ '--count': segments.length })}
    >
      {segments.map((segment, i) => {
        const state = chipState(results?.[i]);
        const description = [
          segment.target && f.targetSound,
          state === 'correct' && f.soundCorrect,
          state === 'practise' && f.soundToPractise,
        ]
          .filter(Boolean)
          .join(', ');
        return (
          <li
            key={i}
            className={cx(styles.chip, segment.target && styles.target, styles[state])}
            style={cssVars({ '--i': i })}
          >
            <span aria-hidden>{segment.letters}</span>
            <span className={styles.srOnly}>
              {segment.letters}
              {description && ` (${description})`}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
